import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { ThemeContext } from './theme-context'
import {
  applyTheme,
  getStoredPreference,
  resolveTheme,
  storePreference,
  type EffectiveTheme,
  type ThemePreference,
} from './theme'

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Stato iniziale letto da localStorage: coerente con quanto già applicato
  // sincronamente in main.tsx, quindi qui non c'è un salto visibile.
  const [preference, setPreferenceState] = useState<ThemePreference>(getStoredPreference)
  const [effective, setEffective] = useState<EffectiveTheme>(() => resolveTheme(preference))

  useEffect(() => {
    const next = resolveTheme(preference)
    setEffective(next)
    applyTheme(next)
  }, [preference])

  // Con preferenza "system", il tema deve seguire il sistema operativo anche
  // se cambia mentre l'app resta aperta — non solo alla prima apertura.
  useEffect(() => {
    if (preference !== 'system' || typeof matchMedia !== 'function') return

    const query = matchMedia('(prefers-color-scheme: light)')
    const onChange = () => {
      const next = resolveTheme('system')
      setEffective(next)
      applyTheme(next)
    }

    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    storePreference(next)
  }, [])

  const value = useMemo(
    () => ({ preference, effective, setPreference }),
    [preference, effective, setPreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
